import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bookingsAPI, reviewsAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import StarRating from '../components/StarRating';
import LoadingSpinner from '../components/LoadingSpinner';

const BookingsListPage = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, review_text: '' });

  useEffect(() => {
    setLoading(true);
    const params = statusFilter ? { status: statusFilter } : {};
    const fetchFn = user.role === 'provider' ? bookingsAPI.getProvider : bookingsAPI.getMy;
    fetchFn(params)
      .then((res) => setBookings(res.data.bookings || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user.role, statusFilter]);

  const handleStatusUpdate = async (bookingId, status) => {
    try {
      await bookingsAPI.updateStatus(bookingId, { status });
      setBookings(bookings.map((b) => b.id === bookingId ? { ...b, status } : b));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    try {
      await reviewsAPI.create({
        provider_id: reviewModal.provider_id,
        booking_id: reviewModal.id,
        service_id: reviewModal.service_id,
        ...reviewForm,
      });
      setReviewModal(null);
      setReviewForm({ rating: 5, review_text: '' });
      alert('Review submitted!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit review');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    accepted: 'bg-primary-100 text-primary-700',
    in_progress: 'bg-purple-100 text-purple-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50/50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">My Bookings</h1>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-sm">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No bookings</h3>
            <Link to="/services" className="text-primary-600 font-medium">Browse Services</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold">{b.service_title}</h3>
                    <p className="text-gray-500 text-sm mt-1">
                      {user.role === 'provider'
                        ? `Customer: ${b.customer_first_name} ${b.customer_last_name}`
                        : `Provider: ${b.provider_first_name} ${b.provider_last_name}`}
                    </p>
                    <p className="text-gray-500 text-sm">
                      📅 {new Date(b.booking_date).toLocaleDateString()} at {b.booking_time}
                    </p>
                    <p className="text-primary-600 font-semibold mt-1">{Number(b.total_price).toLocaleString()} RWF</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[b.status]}`}>
                      {b.status?.replace('_', ' ')}
                    </span>
                    {user.role === 'provider' && b.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => handleStatusUpdate(b.id, 'accepted')}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">Accept</button>
                        <button onClick={() => handleStatusUpdate(b.id, 'cancelled')}
                          className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700">Decline</button>
                      </div>
                    )}
                    {user.role === 'provider' && b.status === 'accepted' && (
                      <button onClick={() => handleStatusUpdate(b.id, 'in_progress')}
                        className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700">Start Service</button>
                    )}
                    {user.role === 'provider' && b.status === 'in_progress' && (
                      <button onClick={() => handleStatusUpdate(b.id, 'completed')}
                        className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">Mark Complete</button>
                    )}
                    {user.role === 'customer' && b.status === 'completed' && (
                      <button onClick={() => setReviewModal(b)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600">Leave Review</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review Modal */}
        {reviewModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4">Leave a Review</h2>
              <form onSubmit={handleReview} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Rating</label>
                  <StarRating rating={reviewForm.rating} onRate={(r) => setReviewForm({ ...reviewForm, rating: r })} size="lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Review (optional)</label>
                  <textarea rows={3} value={reviewForm.review_text}
                    onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                    className="w-full border rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Share your experience..." />
                </div>
                <div className="flex gap-3">
                  <button type="submit" className="flex-1 bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700">Submit</button>
                  <button type="button" onClick={() => setReviewModal(null)} className="flex-1 border py-2.5 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingsListPage;
